// components/common/HeaderStickyDropdown/HeaderStickyDropdown.tsx
import React, { useEffect, useState } from "react";

import { resolveWelcomeKitPurchaseSource } from "@/components/common/WelcomeKit/welcomeKit";
import { DownNavIcon } from "@/components/icons/DownNavIcon";
import { ButtonNova } from "@/components/ui-kit/ButtonNova/ButtonNova";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import { useAtcAreaContext } from "@/context/AtcAreaContext";
import { AtcOption } from "@/types/atc";
import { GA4option } from "@/types/ga4";
import { GA4 } from "@/utils/ga4Utils";
import { cn, getPropertiesForDpa } from "@/utils/general";

import styles from "./HeaderStickyDropdown.module.scss";
import HeaderStickyDropdownOption from "./HeaderStickyDropdownOption";

const atcText = "Add to Cart";

const resolveStickyOptionPurchaseSource = (
  option: AtcOption,
  purchaseSource: string | undefined,
  oneTimePurchaseSource: string | undefined,
  showWelcomeKit: boolean | undefined
): string => {
  const isSubscription = Boolean(option?.subscription_type);
  const sourceForResolve =
    !isSubscription && oneTimePurchaseSource ? oneTimePurchaseSource : purchaseSource || "";
  return resolveWelcomeKitPurchaseSource(sourceForResolve, Boolean(showWelcomeKit), isSubscription);
};

export interface HeaderStickyDropdownProps {
  useOneTimeOption?: boolean;
}

const HeaderStickyDropdown: React.FC<HeaderStickyDropdownProps> = ({
  useOneTimeOption = false,
}: HeaderStickyDropdownProps) => {
  const [isFloat, setIsFloat] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const context = useAtcAreaContext();
  const hasContext = !!context?.atcProducts.length;

  const {
    selectedProduct,
    selectedOptionId,
    setSelectedOptionId,
    purchaseSource,
    promoCode,
    showWelcomeKit,
    oneTimePurchaseSource,
  } = context || {};

  /*
   * Reset ATC button when returning to the page (e.g. back from cart).
   * iOS Safari often does not fire pageshow when restoring from bfcache,
   * so we use visibilitychange as a fallback.
   */
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setIsSubmitted(false);
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") setIsSubmitted(false);
    };
    window.addEventListener("pageshow", handlePageShow);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("pageshow", handlePageShow);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (hasContext) {
      const handleKey = (evt: KeyboardEvent) => {
        evt = evt || (window.event as KeyboardEvent);
        const isEscape = evt && (evt.key === "Escape" || evt.key === "Esc" || evt.keyCode === 27);
        if (isEscape && isFloat) {
          setIsFloat(false);
          const header = document.querySelector("header");
          const main = document.querySelector("main");
          const footer = document.querySelector("footer");
          header?.setAttribute("data-mousedown", "1");
          main?.setAttribute("data-mousedown", "1");
          footer?.setAttribute("data-mousedown", "1");
        }
      };

      const handleClick = (e: MouseEvent) => {
        if (isFloat && !(e.target as Element)?.closest(`.${styles.buttonsHolder}`)) {
          setIsFloat(false);
        }
      };

      document.addEventListener("click", handleClick);
      document.addEventListener("keydown", handleKey);

      return () => {
        document.removeEventListener("click", handleClick);
        document.removeEventListener("keydown", handleKey);
      };
    }
  }, [isFloat, hasContext]);

  if (!hasContext || !selectedProduct?.options?.length) {
    return null;
  }

  const options = selectedProduct ? JSON.parse(JSON.stringify(selectedProduct?.options)) : [];
  const oneTimeOption = selectedProduct.oneTimeOption;
  const alreadyHasOneTime = options.some((option: AtcOption) => option.sku === oneTimeOption?.sku);
  if (useOneTimeOption && oneTimeOption && !alreadyHasOneTime) {
    options.push(oneTimeOption);
  }
  const activeOption = options?.[selectedOptionId];

  if (!activeOption) {
    return null;
  }

  const atcClick = () => {
    if (isSubmitted) {
      return;
    }
    if (isFloat) {
      setIsFloat(false);
    }

    setIsSubmitted(true);

    const resolvedPurchaseSource = resolveStickyOptionPurchaseSource(
      activeOption,
      purchaseSource,
      oneTimePurchaseSource,
      showWelcomeKit
    );

    GA4.onProductAdded({
      options: [activeOption],
      location: "header stickynav",
      type: "button_stickynav",
      purchase_source: resolvedPurchaseSource,
    });

    const { dpaSfx } = getPropertiesForDpa(activeOption);
    const code = promoCode || activeOption?.ui_data?.code;
    const codeSfx = code ? `&code=${code}` : "";

    const dpaUrl =
      process.env.NEXT_PUBLIC_MAGENTO_URL +
      `dpa/add/tocart/?skus=${activeOption.sku}${dpaSfx}${codeSfx}&purchase_source=${resolvedPurchaseSource}`;

    window.location.href = dpaUrl;
  };

  const onSelect = (idx: number) => {
    const option = options[idx];
    GA4.onProductSelected({
      option,
      type: "dropdown_stickynav",
      purchase_source: resolveStickyOptionPurchaseSource(
        option,
        purchaseSource,
        oneTimePurchaseSource,
        showWelcomeKit
      ),
    });
    setSelectedOptionId(idx);
    setIsFloat(false);
  };

  const onOpen = () => {
    const optionsWithSources = options.map((option: AtcOption) => ({
      ...option,
      purchase_source: resolveStickyOptionPurchaseSource(
        option,
        purchaseSource,
        oneTimePurchaseSource,
        showWelcomeKit
      ),
    }));
    GA4.onViewItemListOptions({
      options: optionsWithSources as GA4option[],
      purchase_source: purchaseSource,
    });
  };

  const mapper =
    options && options.map
      ? options.map((el: AtcOption, idx: number) => (
          <HeaderStickyDropdownOption
            key={idx}
            option={el}
            index={idx}
            type="float"
            selectedOptionId={selectedOptionId}
            onSelect={() => onSelect(idx)}
          />
        ))
      : null;

  return (
    <div className={cn(styles.altContentsContainer, "flex justify-end items-center")}>
      <div className={cn(styles.buttonsWrap, "ml-auto flex justify-end w-full")}>
        <div className={cn(styles.buttonsHolder, "relative h-[44px] rounded-md my-0 mr-1 p-0")}>
          <HeaderStickyDropdownOption
            option={options[selectedOptionId]}
            index={selectedOptionId}
            type="normal"
            onSelect={() => {
              setIsFloat(true);
              onOpen();
            }}
          />
          {isFloat && (
            <div className={styles.buttonsFloat}>
              <button
                aria-label="Choose an Option"
                className={cn(styles.chooser, "flex w-full md:max-w-[252px] h-[42px] pt-3")}
                onClick={() => setIsFloat(false)}
              >
                <span className="flex-1 w-full text-left">
                  <TextElementNova
                    element="span"
                    text={`Choose an Option`}
                    className="Caption-10-Dia-Uppercase"
                  />
                </span>
                <span>
                  <DownNavIcon className="text-nova-base-black w-[24px]" />
                </span>
              </button>
              {mapper}
            </div>
          )}
        </div>
        <ButtonNova
          className={cn(styles.atcButton, {
            disabled: isSubmitted,
          })}
          level={`Primary`}
          label={atcText}
          onClick={atcClick}
        />
      </div>
    </div>
  );
};

export default HeaderStickyDropdown;
